import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import PageLayout from "../../components/layout/PageLayout";

import { extractList } from "../../utils/response";
import { useServerValidationErrors } from "../../hooks/useServerValidationErrors";
import { toast } from "react-toastify";

import {
  useCreateServiceStatePriceMutation,
  useGetServiceStatePriceByIdQuery,
  useUpdateServiceStatePriceMutation,
} from "../../store/api/serviceStatePriceApi";

import { SERVICE_STATE_PRICES_LIST_PATH } from "../../navigations/path";
import { useLazyGetServicePlansQuery } from "../../store/api/servicePlansApi";
import {
  useLazyGetCountriesQuery,
  useLazyGetCurrenciesQuery,
  useLazyGetServicesQuery,
  useLazyGetStatesByCountryIdQuery,
  //   useLazyGetStatesQuery,
} from "../../store/api";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const buildFormData = (values) => {
  const fd = new FormData();

  fd.append("country_id", values.country_id ?? "");
  fd.append("state_id", values.state_id ?? "");
  fd.append("service_plan_id", values.service_plan_id ?? "");
  fd.append("service_id", values.service_id ?? "");
  fd.append("currency_id", values.currency_id ?? "");

  fd.append("price", String(values.price ?? ""));
  fd.append("government_fee", String(values.government_fee ?? ""));
  fd.append("description", values.description ?? "");

  return fd;
};

const optionLabel = (item) => {
  if (!item) return "";

  return (
    item.label ??
    item.name ??
    item.title ??
    item.currency_name ??
    item.code ??
    String(item.id ?? "")
  );
};

const normalizeOption = (item) => ({
  id: item?.id,
  label: optionLabel(item),
  raw: item,
});

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const CreateServiceStatePrice = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(editId);

  /* ------------------------------ Form State ----------------------------- */

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedServicePlan, setSelectedServicePlan] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [price, setPrice] = useState("");
  const [governmentFee, setGovernmentFee] = useState("");
  const [description, setDescription] = useState("");

  const [hydrated, setHydrated] = useState(false);

  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  /* ---------------------------------------------------------------------- */
  /* Dropdown APIs                                                          */
  /* ---------------------------------------------------------------------- */

  const [fetchCountries, countriesQuery] = useLazyGetCountriesQuery();

  //   const [fetchStates, statesQuery] = useLazyGetStatesQuery();
  const [fetchStatesByCountryId, statesQuery] =
    useLazyGetStatesByCountryIdQuery();

  const [fetchServicePlans, servicePlansQuery] = useLazyGetServicePlansQuery();

  const [fetchServices, servicesQuery] = useLazyGetServicesQuery();

  const [fetchCurrencies, currenciesQuery] = useLazyGetCurrenciesQuery();

  /* ---------------------------------------------------------------------- */
  /* Dropdown Options                                                       */
  /* ---------------------------------------------------------------------- */

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [services, setServices] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  /* ---------------------------------------------------------------------- */
  /* Load Countries                                                         */
  /* ---------------------------------------------------------------------- */

  const loadCountries = useCallback(async () => {
    try {
      const response = await fetchCountries({
        paginate: 0,
      }).unwrap();

      const list = extractList(response);

      const options = Array.isArray(list) ? list.map(normalizeOption) : [];

      setCountries(options);
    } catch (error) {
      toast.error(error?.data?.message || "Could not load countries");
    }
  }, [fetchCountries]);

  /* ---------------------------------------------------------------------- */
  /* Load States                                                             */
  /* ---------------------------------------------------------------------- */

  const loadStates = useCallback(
    async (countryId) => {
      if (!countryId) {
        setStates([]);
        return;
      }

      try {
        const response = await fetchStatesByCountryId(countryId).unwrap();

        console.log("STATES RESPONSE:", response);

        const list =
          response?.data?.list ??
          response?.list ??
          response?.data ??
          response ??
          [];

        const options = Array.isArray(list)
          ? list.map((item) => ({
              id: item.uuid,
              label: item.name ?? item.label ?? item.title ?? String(item.id),
              raw: item,
            }))
          : [];

        setStates(options);
      } catch (error) {
        console.error("STATES ERROR:", error);

        setStates([]);

        toast.error(error?.data?.message || "Could not load states");
      }
    },
    [fetchStatesByCountryId],
  );

  /* ---------------------------------------------------------------------- */
  /* Load Service Plans                                                      */
  /* ---------------------------------------------------------------------- */

  const loadServicePlans = useCallback(async () => {
    try {
      const response = await fetchServicePlans({
        paginate: 0,
      }).unwrap();

      const list = extractList(response);

      const options = Array.isArray(list) ? list.map(normalizeOption) : [];

      setServicePlans(options);
    } catch (error) {
      toast.error(error?.data?.message || "Could not load service plans");
    }
  }, [fetchServicePlans]);

  /* ---------------------------------------------------------------------- */
  /* Load Services                                                           */
  /* ---------------------------------------------------------------------- */

  const loadServices = useCallback(async () => {
    try {
      const response = await fetchServices({
        paginate: 0,
      }).unwrap();

      const list = extractList(response);

      const options = Array.isArray(list) ? list.map(normalizeOption) : [];

      setServices(options);
    } catch (error) {
      toast.error(error?.data?.message || "Could not load services");
    }
  }, [fetchServices]);

  /* ---------------------------------------------------------------------- */
  /* Load Currencies                                                         */
  /* ---------------------------------------------------------------------- */

  const loadCurrencies = useCallback(async () => {
    try {
      const response = await fetchCurrencies({
        paginate: 0,
      }).unwrap();

      const list = extractList(response);

      const options = Array.isArray(list) ? list.map(normalizeOption) : [];

      setCurrencies(options);
    } catch (error) {
      toast.error(error?.data?.message || "Could not load currencies");
    }
  }, [fetchCurrencies]);

  /* ---------------------------------------------------------------------- */
  /* Service State Price API                                                 */
  /* ---------------------------------------------------------------------- */

  const {
    data: serviceStatePriceData,
    isLoading: serviceStatePriceLoading,
    isError: serviceStatePriceError,
    error: serviceStatePriceErr,
  } = useGetServiceStatePriceByIdQuery(
    { id: editId },
    {
      skip: !isEdit || !editId,
    },
  );

  const [createServiceStatePrice, { isLoading: creating }] =
    useCreateServiceStatePriceMutation();

  const [updateServiceStatePrice, { isLoading: updating }] =
    useUpdateServiceStatePriceMutation();

  const submitting = creating || updating;

  const loadingPage = isEdit && (serviceStatePriceLoading || !hydrated);

  /* ---------------------------------------------------------------------- */
  /* Load Error                                                              */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!serviceStatePriceError) return;

    toast.error(
      serviceStatePriceErr?.data?.message ||
        "Failed to load service state price",
    );

    navigate(SERVICE_STATE_PRICES_LIST_PATH);
  }, [serviceStatePriceError, serviceStatePriceErr, navigate]);

  /* ---------------------------------------------------------------------- */
  /* Edit Data Hydration                                                     */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!isEdit || !serviceStatePriceData || hydrated) {
      return;
    }

    const item = serviceStatePriceData?.data ?? serviceStatePriceData;

    if (!item) return;

    /* ------------------------------ Country ----------------------------- */

    const country =
      item.state.country && typeof item.state.country === "object"
        ? item.state.country
        : null;

    const countryId = item.country_id ?? country?.id;

    if (countryId) {
      const countryOption = normalizeOption(
        country || {
          id: countryId,
        },
      );

      setSelectedCountry(countryOption);

      setCountries((prev) => {
        if (prev.some((option) => option.id === countryId)) {
          return prev;
        }

        return [...prev, countryOption];
      });
    }

    /* ------------------------------- State ------------------------------ */

    const state =
      item.state && typeof item.state === "object" ? item.state : null;

    const stateId = item.state_id ?? state?.id;

    if (stateId) {
      const stateOption = normalizeOption(
        state || {
          id: stateId,
        },
      );

      setSelectedState(stateOption);

      setStates((prev) => {
        if (prev.some((option) => option.id === stateId)) {
          return prev;
        }

        return [...prev, stateOption];
      });
    }

    /* --------------------------- Service Plan -------------------------- */

    const servicePlan =
      item.service_plan && typeof item.service_plan === "object"
        ? item.service_plan
        : null;

    const servicePlanId = item.service_plan_id ?? servicePlan?.id;

    if (servicePlanId) {
      const option = normalizeOption(
        servicePlan || {
          id: servicePlanId,
        },
      );

      setSelectedServicePlan(option);

      setServicePlans((prev) => {
        if (prev.some((item) => item.id === servicePlanId)) {
          return prev;
        }

        return [...prev, option];
      });
    }

    /* ------------------------------- Service ---------------------------- */

    const service =
      item.service && typeof item.service === "object" ? item.service : null;

    const serviceId = item.service_id ?? service?.id;

    if (serviceId) {
      const option = normalizeOption(
        service || {
          id: serviceId,
        },
      );

      setSelectedService(option);

      setServices((prev) => {
        if (prev.some((item) => item.id === serviceId)) {
          return prev;
        }

        return [...prev, option];
      });
    }

    /* ------------------------------ Currency ---------------------------- */

    const currency =
      item.currency && typeof item.currency === "object" ? item.currency : null;

    const currencyId = item.currency_id ?? currency?.id;

    if (currencyId) {
      const option = normalizeOption(
        currency || {
          id: currencyId,
        },
      );

      setSelectedCurrency(option);

      setCurrencies((prev) => {
        if (prev.some((item) => item.id === currencyId)) {
          return prev;
        }

        return [...prev, option];
      });
    }

    /* ------------------------------- Fields ----------------------------- */

    setPrice(item.price != null ? String(item.price) : "");

    setGovernmentFee(
      item.government_fee != null ? String(item.government_fee) : "",
    );

    setDescription(item.description ?? "");

    setHydrated(true);
  }, [isEdit, serviceStatePriceData, hydrated]);

  /* ---------------------------------------------------------------------- */
  /* Load edit State options after Country is available                      */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!isEdit || !serviceStatePriceData) {
      return;
    }

    const item = serviceStatePriceData?.data ?? serviceStatePriceData;

    const countryId = item?.country_id ?? item?.country?.id;

    if (countryId) {
      loadStates(countryId);
    }
  }, [isEdit, serviceStatePriceData, loadStates]);

  /* ---------------------------------------------------------------------- */
  /* Country Change                                                          */
  /* ---------------------------------------------------------------------- */

  const handleCountryChange = async (_, value) => {
    setSelectedCountry(value);

    clearError("country_id");

    // Country change means old state is no longer valid
    setSelectedState(null);
    setStates([]);

    clearError("state_id");

    if (value?.id) {
      await loadStates(value.id);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Submit                                                                  */
  /* ---------------------------------------------------------------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* -------------------------- Client Validation ----------------------- */

    const clientErrors = {};

    if (!selectedCountry?.id) {
      clientErrors.country_id = "Country is required";
    }

    if (!selectedState?.id) {
      clientErrors.state_id = "State is required";
    }

    if (!selectedServicePlan?.id) {
      clientErrors.service_plan_id = "Service plan is required";
    }

    if (!selectedService?.id) {
      clientErrors.service_id = "Service is required";
    }

    if (!selectedCurrency?.id) {
      clientErrors.currency_id = "Currency is required";
    }

    if (price === "" || Number.isNaN(Number(price))) {
      clientErrors.price = "Enter valid price";
    }

    if (governmentFee === "" || Number.isNaN(Number(governmentFee))) {
      clientErrors.government_fee = "Enter valid government fee";
    }

    setErrors(clientErrors);

    if (Object.keys(clientErrors).length > 0) {
      return;
    }

    /* ----------------------------- Form Values ------------------------- */

    const values = {
      country_id: selectedCountry.id,
      state_id: selectedState.id,
      service_plan_id: selectedServicePlan.id,
      service_id: selectedService.id,
      currency_id: selectedCurrency.id,

      price: price !== "" ? Number(price) : null,

      government_fee: governmentFee !== "" ? Number(governmentFee) : null,

      description: description.trim(),
    };

    const fd = buildFormData(values);

    /* ------------------------------- API -------------------------------- */

    try {
      if (isEdit) {
        await updateServiceStatePrice({
          id: editId,
          data: fd,
        }).unwrap();

        toast.success("Service state price updated");
      } else {
        await createServiceStatePrice(fd).unwrap();

        toast.success("Service state price added");
      }

      navigate(SERVICE_STATE_PRICES_LIST_PATH);
    } catch (err) {
      applyApiError(
        err,
        isEdit
          ? "Failed to update service state price"
          : "Failed to add service state price",
      );
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Loading Page                                                            */
  /* ---------------------------------------------------------------------- */

  if (loadingPage) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* UI                                                                      */
  /* ---------------------------------------------------------------------- */

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back Button */}

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(SERVICE_STATE_PRICES_LIST_PATH)}
          sx={{ mb: 2 }}
        >
          Back to service state prices
        </Button>

        {/* Heading */}

        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          {isEdit ? "Edit service state price" : "Add service state price"}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* ------------------------------------------------ */}
              {/* Country                                         */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countries}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  onOpen={countries.length === 0 ? loadCountries : undefined}
                  loading={
                    countriesQuery.isLoading || countriesQuery.isFetching
                  }
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      fullWidth
                      {...fieldProps("country_id")}
                    />
                  )}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* State                                            */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={states}
                  value={selectedState}
                  onChange={(_, value) => {
                    setSelectedState(value);

                    clearError("state_id");
                  }}
                  disabled={!selectedCountry?.id}
                  loading={statesQuery.isLoading || statesQuery.isFetching}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      required
                      fullWidth
                      {...fieldProps("state_id")}
                      helperText={
                        !selectedCountry?.id
                          ? "Select country first"
                          : fieldProps("state_id").helperText
                      }
                    />
                  )}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Service Plan                                     */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={servicePlans}
                  value={selectedServicePlan}
                  onChange={(_, value) => {
                    setSelectedServicePlan(value);

                    clearError("service_plan_id");
                  }}
                  onOpen={() => {
                    if (servicePlans.length === 0) {
                      loadServicePlans();
                    }
                  }}
                  loading={
                    servicePlansQuery.isLoading || servicePlansQuery.isFetching
                  }
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Service Plan"
                      required
                      fullWidth
                      {...fieldProps("service_plan_id")}
                    />
                  )}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Service                                          */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={services}
                  value={selectedService}
                  onChange={(_, value) => {
                    setSelectedService(value);

                    clearError("service_id");
                  }}
                  onOpen={() => {
                    if (services.length === 0) {
                      loadServices();
                    }
                  }}
                  loading={servicesQuery.isLoading || servicesQuery.isFetching}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Service"
                      required
                      fullWidth
                      {...fieldProps("service_id")}
                    />
                  )}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Currency                                         */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={currencies}
                  value={selectedCurrency}
                  onChange={(_, value) => {
                    setSelectedCurrency(value);

                    clearError("currency_id");
                  }}
                  onOpen={() => {
                    if (currencies.length === 0) {
                      loadCurrencies();
                    }
                  }}
                  loading={
                    currenciesQuery.isLoading || currenciesQuery.isFetching
                  }
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      required
                      fullWidth
                      {...fieldProps("currency_id")}
                    />
                  )}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Price                                             */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);

                    clearError("price");
                  }}
                  required
                  fullWidth
                  type="number"
                  inputProps={{
                    min: 0,
                    step: "0.01",
                  }}
                  {...fieldProps("price")}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Government Fee                                    */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Government Fee"
                  value={governmentFee}
                  onChange={(e) => {
                    setGovernmentFee(e.target.value);

                    clearError("government_fee");
                  }}
                  required
                  fullWidth
                  type="number"
                  inputProps={{
                    min: 0,
                    step: "0.01",
                  }}
                  {...fieldProps("government_fee")}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Description                                      */}
              {/* ------------------------------------------------ */}

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);

                    clearError("description");
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps("description")}
                />
              </Grid>

              {/* ------------------------------------------------ */}
              {/* Buttons                                          */}
              {/* ------------------------------------------------ */}

              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    "Save changes"
                  ) : (
                    "Add service state price"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(SERVICE_STATE_PRICES_LIST_PATH)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateServiceStatePrice;
